/**
 * Weaponeering API Client
 *
 * Provides a clean interface to the weaponeering backend API.
 * Handles sites, facilities, DMPIs, ordnance, and model-written suggestions.
 */

import { apiErrorFromResponse } from './apiError';

class WeaponeeringAPIClient {
  constructor(baseURL = null) {
    // Use VITE_API_BASE_URL and append /api, matching ApiService.js pattern
    const baseUrl = baseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    this.baseURL = `${baseUrl}/api`;
    this.authToken = null;
    this.tokenProvider = null;
  }

  /**
   * Set Auth0 access token for API requests
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Clear Auth0 access token
   */
  clearAuthToken() {
    this.authToken = null;
  }

  /**
   * Register an async token provider (Auth0 getAccessTokenSilently). Called
   * before each request to obtain a valid access token on demand — matches the
   * APIClient/ApiService pattern so protected endpoints don't 403 on a
   * transiently-expired cached token.
   */
  setTokenProvider(fn) {
    this.tokenProvider = fn;
  }

  clearTokenProvider() {
    this.tokenProvider = null;
  }

  /**
   * Resolve a valid bearer token: prefer an on-demand fresh token from the
   * provider (cached-if-valid by the Auth0 SDK), falling back to the last synced
   * token. Never throws — a null result just means no Authorization header.
   */
  async resolveToken() {
    if (this.tokenProvider) {
      try {
        const token = await this.tokenProvider();
        if (token) {
          this.authToken = token; // keep the synced copy fresh
          return token;
        }
      } catch (err) {
        console.warn('⚠️ Could not obtain a fresh access token:', err?.message || err);
      }
    }
    return this.authToken;
  }

  /**
   * Generic request wrapper with error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Resolve a valid token on demand (fresh from Auth0 when possible) so a
    // cleared/expired cached token doesn't cause protected calls to 401/403.
    const token = await this.resolveToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw await apiErrorFromResponse(response);
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`Weaponeering API Error [${endpoint}]:`, error.message);
      throw error;
    }
  }

  // ==================== Sites ====================

  /**
   * Get geographic filter hierarchy with counts and bounds
   * @returns {Promise<Object>} Filter hierarchy with COCOMs, theaters, focus areas
   */
  async getFilterHierarchy() {
    return this.request('/weaponeering/sites/filter-hierarchy');
  }

  /**
   * List weaponeering sites with optional filters
   * @param {Object} filters - Optional filters
   * @param {string} filters.lifecycle_stage - Filter by lifecycle stage
   * @param {string} filters.cocom - Filter by COCOM
   * @param {string} filters.theater - Filter by theater
   * @param {string} filters.focus_area - Filter by focus area
   * @param {string} filters.quadrant - Filter by quadrant
   * @param {string} filters.analyst_assigned - Filter by assigned analyst
   * @param {boolean} filters.ai_suggested - Filter by AI-suggested sites
   * @param {number} filters.limit - Max results (default 100)
   * @param {number} filters.offset - Pagination offset (default 0)
   * @returns {Promise<Object>} Sites list with total count
   */
  async listSites(filters = {}) {
    const params = new URLSearchParams();
    if (filters.lifecycle_stage) params.append('lifecycle_stage', filters.lifecycle_stage);
    if (filters.cocom) params.append('cocom', filters.cocom);
    if (filters.theater) params.append('theater', filters.theater);
    if (filters.focus_area) params.append('focus_area', filters.focus_area);
    if (filters.quadrant) params.append('quadrant', filters.quadrant);
    if (filters.analyst_assigned) params.append('analyst_assigned', filters.analyst_assigned);
    if (filters.ai_suggested !== undefined) params.append('ai_suggested', filters.ai_suggested);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const endpoint = queryString ? `/weaponeering/sites?${queryString}` : '/weaponeering/sites';

    return this.request(endpoint);
  }

  /**
   * Get specific site by ID
   * @param {string} siteId - Site UUID
   * @returns {Promise<Object>} Site object
   */
  async getSite(siteId) {
    return this.request(`/weaponeering/sites/${siteId}`);
  }

  /**
   * Create new weaponeering site
   * @param {Object} siteData - Site creation data
   * @returns {Promise<Object>} Created site
   */
  async createSite(siteData) {
    console.log('🆕 Creating weaponeering site:', siteData.name);
    return this.request('/weaponeering/sites', {
      method: 'POST',
      body: JSON.stringify(siteData),
    });
  }

  /**
   * Update site
   * @param {string} siteId - Site UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated site
   */
  async updateSite(siteId, updates) {
    return this.request(`/weaponeering/sites/${siteId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Query sites within a geographic region
   * @param {Object} params - Query parameters
   * @param {number} params.lat - Center latitude
   * @param {number} params.lon - Center longitude
   * @param {number} params.radius_km - Search radius in kilometers
   * @param {string} params.theater - Optional theater filter (column renamed from theater_id)
   * @param {boolean} params.include_lifecycle_summary - Include breakdown by lifecycle stage
   * @returns {Promise<Object>} Regional sites response with mission-ready and in-development counts
   */
  async querySitesInRegion({ lat, lon, radius_km, theater, include_lifecycle_summary = true }) {
    const params = new URLSearchParams();
    params.append('lat', lat);
    params.append('lon', lon);
    params.append('radius_km', radius_km);
    if (theater) params.append('theater', theater);
    if (include_lifecycle_summary) params.append('include_lifecycle_summary', 'true');

    return this.request(`/weaponeering/sites/in-region?${params.toString()}`);
  }

  /**
   * Delete site (cascades to facilities and DMPIs)
   * @param {string} siteId - Site UUID
   * @returns {Promise<null>}
   */
  async deleteSite(siteId) {
    return this.request(`/weaponeering/sites/${siteId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Site Verification ====================

  /**
   * Verify that site coordinates are correct
   * @param {string} siteId - Site UUID
   * @param {Object} verificationData - Verification data
   * @param {string} verificationData.verified_by - Analyst email/ID
   * @param {string} verificationData.verification_notes - Optional verification notes
   * @returns {Promise<Object>} Verification response
   */
  async verifySiteLocation(siteId, verificationData) {
    console.log('✅ Verifying site location:', siteId);
    return this.request(`/weaponeering/sites/${siteId}/verify`, {
      method: 'POST',
      body: JSON.stringify(verificationData),
    });
  }

  /**
   * Adjust site coordinates to correct location
   * @param {string} siteId - Site UUID
   * @param {Object} adjustmentData - Adjustment data
   * @param {number} adjustmentData.new_coordinates_lat - New latitude
   * @param {number} adjustmentData.new_coordinates_lon - New longitude
   * @param {string} adjustmentData.adjustment_reason - Reason for adjustment
   * @param {string} adjustmentData.verified_by - Analyst email/ID
   * @param {string} adjustmentData.verification_notes - Optional verification notes
   * @returns {Promise<Object>} Adjustment response with original coordinates preserved
   */
  async adjustSiteCoordinates(siteId, adjustmentData) {
    console.log('📍 Adjusting site coordinates:', siteId);
    return this.request(`/weaponeering/sites/${siteId}/adjust`, {
      method: 'POST',
      body: JSON.stringify(adjustmentData),
    });
  }

  /**
   * Disqualify a site and return it to raw_intelligence stage
   * @param {string} siteId - Site UUID
   * @param {Object} disqualificationData - Disqualification data
   * @param {string} disqualificationData.disqualification_reason - Reason for disqualification
   * @param {string} disqualificationData.verified_by - Analyst email/ID
   * @returns {Promise<Object>} Disqualification response
   */
  async disqualifySite(siteId, disqualificationData) {
    console.log('⚠️ Disqualifying site:', siteId);
    return this.request(`/weaponeering/sites/${siteId}/disqualify`, {
      method: 'POST',
      body: JSON.stringify(disqualificationData),
    });
  }

  // ==================== Satellite Imagery ====================

  /**
   * Fetch satellite imagery for a site
   * @param {string} siteId - Site UUID
   * @param {string} imageryProvider - Optional provider (sentinel, maxar, planet)
   * @param {number} resolutionMeters - Optional resolution in meters
   * @returns {Promise<Object>} Imagery response with URL and metadata
   */
  async getSiteImagery(siteId, imageryProvider = null, resolutionMeters = null) {
    console.log('📷 Fetching satellite imagery for site:', siteId);
    const params = new URLSearchParams();
    if (imageryProvider) params.append('imagery_provider', imageryProvider);
    if (resolutionMeters) params.append('resolution_meters', resolutionMeters);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/weaponeering/imagery/sites/${siteId}?${queryString}`
      : `/weaponeering/imagery/sites/${siteId}`;

    return this.request(endpoint);
  }

  /**
   * Analyze drawn area using computer vision to detect facilities
   * @param {Object} analysisData - Analysis request data
   * @param {string} analysisData.site_id - Site UUID
   * @param {Object} analysisData.polygon_geojson - Drawn polygon in GeoJSON format
   * @param {string} analysisData.imagery_url - Satellite imagery URL to analyze
   * @param {string} analysisData.analysis_type - Optional analysis type (default: facility_detection)
   * @returns {Promise<Object>} Analysis response with detected facilities
   */
  async analyzeAreaForFacilities(analysisData) {
    console.log('🤖 Analyzing area for facilities:', analysisData.site_id);
    return this.request('/weaponeering/imagery/analyze-area', {
      method: 'POST',
      body: JSON.stringify(analysisData),
    });
  }

  /**
   * Generate per-stop ISR observations for a reconnaissance fly-through.
   * Interrogates site OSINT + imagery to explain why each vantage was chosen
   * and what is of interest in view.
   * @param {Object} payload
   * @param {string} payload.site_id - Site UUID
   * @param {string} [payload.imagery_url] - Optional satellite imagery URL for vision grounding
   * @param {Array<Object>} payload.stops - Ordered camera stops to narrate
   * @param {Object} [options] - fetch options (e.g. { signal })
   * @returns {Promise<Object>} { site_id, narrations: [{ index, observation }], model }
   */
  async narrateTour(payload, options = {}) {
    console.log('🎥 Narrating reconnaissance tour:', payload.site_id);
    return this.request('/weaponeering/imagery/narrate-tour', {
      method: 'POST',
      body: JSON.stringify(payload),
      ...options,
    });
  }

  // ==================== Facilities ====================

  /**
   * List facilities with optional filters
   * @param {Object} filters - Optional filters
   * @param {string} filters.site_id - Filter by parent site
   * @param {string} filters.lifecycle_stage - Filter by lifecycle stage
   * @param {string} filters.hardness - Filter by target hardness
   * @param {string} filters.facility_type - Filter by facility type
   * @param {number} filters.limit - Max results
   * @param {number} filters.offset - Pagination offset
   * @returns {Promise<Object>} Facilities list with total count
   */
  async listFacilities(filters = {}) {
    const params = new URLSearchParams();
    if (filters.site_id) params.append('site_id', filters.site_id);
    if (filters.lifecycle_stage) params.append('lifecycle_stage', filters.lifecycle_stage);
    if (filters.dmpi_lifecycle_stage) params.append('dmpi_lifecycle_stage', filters.dmpi_lifecycle_stage);
    if (filters.cocom) params.append('cocom', filters.cocom);
    if (filters.theater) params.append('theater', filters.theater);
    if (filters.focus_area) params.append('focus_area', filters.focus_area);
    if (filters.hardness) params.append('hardness', filters.hardness);
    if (filters.facility_type) params.append('facility_type', filters.facility_type);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const endpoint = queryString ? `/weaponeering/facilities?${queryString}` : '/weaponeering/facilities';

    return this.request(endpoint);
  }

  /**
   * Get specific facility by ID
   * @param {string} facilityId - Facility UUID
   * @returns {Promise<Object>} Facility object
   */
  async getFacility(facilityId) {
    return this.request(`/weaponeering/facilities/${facilityId}`);
  }

  /**
   * Create new facility
   * @param {Object} facilityData - Facility creation data
   * @returns {Promise<Object>} Created facility
   */
  async createFacility(facilityData) {
    console.log('🆕 Creating facility:', facilityData.name);
    return this.request('/weaponeering/facilities', {
      method: 'POST',
      body: JSON.stringify(facilityData),
    });
  }

  /**
   * Update facility
   * @param {string} facilityId - Facility UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated facility
   */
  async updateFacility(facilityId, updates) {
    return this.request(`/weaponeering/facilities/${facilityId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete facility (cascades to DMPIs)
   * @param {string} facilityId - Facility UUID
   * @returns {Promise<null>}
   */
  async deleteFacility(facilityId) {
    return this.request(`/weaponeering/facilities/${facilityId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Promote facility to mission catalog
   * @param {string} facilityId - Facility UUID
   * @returns {Promise<Object>} Promoted facility with mission_target_id
   */
  async promoteFacility(facilityId) {
    console.log('🚀 Promoting facility to mission catalog:', facilityId);
    return this.request(`/weaponeering/facilities/${facilityId}/promote`, {
      method: 'POST',
    });
  }

  // ==================== DMPIs ====================

  /**
   * List DMPIs with optional filters
   * @param {Object} filters - Optional filters
   * @param {string} filters.facility_id - Filter by parent facility
   * @param {string} filters.lifecycle_stage - Filter by lifecycle stage
   * @param {string} filters.desired_effect - Filter by desired effect
   * @param {number} filters.limit - Max results
   * @param {number} filters.offset - Pagination offset
   * @returns {Promise<Object>} DMPIs list with total count
   */
  async listDMPIs(filters = {}) {
    const params = new URLSearchParams();
    if (filters.facility_id) params.append('facility_id', filters.facility_id);
    if (filters.lifecycle_stage) params.append('lifecycle_stage', filters.lifecycle_stage);
    if (filters.desired_effect) params.append('desired_effect', filters.desired_effect);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const endpoint = queryString ? `/weaponeering/dmpis?${queryString}` : '/weaponeering/dmpis';

    return this.request(endpoint);
  }

  /**
   * Get specific DMPI by ID
   * @param {string} dmpiId - DMPI UUID
   * @returns {Promise<Object>} DMPI object
   */
  async getDMPI(dmpiId) {
    return this.request(`/weaponeering/dmpis/${dmpiId}`);
  }

  /**
   * Create new DMPI
   * @param {Object} dmpiData - DMPI creation data
   * @returns {Promise<Object>} Created DMPI
   */
  async createDMPI(dmpiData) {
    console.log('🆕 Creating DMPI:', dmpiData.name);
    return this.request('/weaponeering/dmpis', {
      method: 'POST',
      body: JSON.stringify(dmpiData),
    });
  }

  /**
   * Update DMPI
   * @param {string} dmpiId - DMPI UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated DMPI
   */
  async updateDMPI(dmpiId, updates) {
    return this.request(`/weaponeering/dmpis/${dmpiId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete DMPI
   * @param {string} dmpiId - DMPI UUID
   * @returns {Promise<null>}
   */
  async deleteDMPI(dmpiId) {
    return this.request(`/weaponeering/dmpis/${dmpiId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Assign ordnance to DMPI
   * @param {string} dmpiId - DMPI UUID
   * @param {string} ordnanceType - Ordnance ID (e.g., 'tlam_d')
   * @param {number} missileCount - Number of missiles
   * @param {Object} aiTracking - Optional AI tracking fields
   * @returns {Promise<Object>} Updated DMPI with ordnance assignment
   */
  async assignOrdnance(dmpiId, ordnanceType, missileCount, aiTracking = null) {
    console.log(`💣 Assigning ordnance to DMPI ${dmpiId}: ${missileCount}x ${ordnanceType}`);
    return this.request(`/weaponeering/dmpis/${dmpiId}/ordnance`, {
      method: 'PATCH',
      body: JSON.stringify({
        ordnance_type: ordnanceType,
        missile_count: missileCount,
        ...aiTracking
      }),
    });
  }

  /**
   * Real, server-computed terminal dive-angle recommendation for a DMPI —
   * grounded in the target facility's real hardness and (if already
   * assigned) the real ordnance's warhead_type, via
   * terminal_angle_estimator.recommend_dive_angle. Not a client-side guess.
   * @param {string} dmpiId - DMPI UUID
   */
  async recommendTerminalAngle(dmpiId) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/recommend-terminal-angle`, {
      method: 'POST',
    });
  }

  /**
   * Score a DMPI's Collateral Hazard Area against enumerated structures.
   * @param {string} dmpiId - DMPI UUID
   * @param {Array<{lat:number, lon:number, category:string, subtype:string, distance_m:number}>} structures
   */
  async assessCollateralRisk(dmpiId, structures) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/assess-collateral-risk`, {
      method: 'POST',
      body: JSON.stringify({ structures }),
    });
  }

  /**
   * Command-authority "accept the risk" override for a DMPI at the
   * command_review CDE tier.
   */
  async acceptCollateralRisk(dmpiId, rationale) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/accept-collateral-risk`, {
      method: 'POST',
      body: JSON.stringify({ rationale }),
    });
  }

  async getCollateralObjects(dmpiId) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/collateral-objects`);
  }

  async linkStrikePlan(dmpiId, strikePackageId, executionWindowAt = null) {
    const params = new URLSearchParams({ strike_package_id: strikePackageId });
    if (executionWindowAt) params.append('execution_window_at', executionWindowAt);
    return this.request(`/weaponeering/dmpis/${dmpiId}/link-strike-plan?${params}`, { method: 'POST' });
  }

  async submitBDA(dmpiId, bdaData) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/bda`, {
      method: 'POST',
      body: JSON.stringify(bdaData),
    });
  }

  async listBDAReports(dmpiId) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/bda`);
  }

  async getTheaterOverview(filters = {}) {
    const params = new URLSearchParams();
    if (filters.cocom) params.append('cocom', filters.cocom);
    if (filters.theater) params.append('theater', filters.theater);
    if (filters.focus_area) params.append('focus_area', filters.focus_area);
    const qs = params.toString();
    return this.request(`/weaponeering/dmpis/theater-overview${qs ? '?' + qs : ''}`);
  }

  // ==================== Ordnance Catalog ====================

  /**
   * List available ordnance with optional filters
   * @param {Object} filters - Optional filters
   * @param {string} filters.ordnance_type - Filter by type (cruise_missile, etc.)
   * @param {string} filters.platform_type - Filter by platform compatibility
   * @param {number} filters.min_range_km - Minimum range filter
   * @param {number} filters.max_range_km - Maximum range filter
   * @param {boolean} filters.active_only - Only active ordnance (default true)
   * @returns {Promise<Object>} Ordnance list
   */
  async listOrdnance(filters = {}) {
    const params = new URLSearchParams();
    if (filters.ordnance_type) params.append('ordnance_type', filters.ordnance_type);
    if (filters.platform_type) params.append('platform_type', filters.platform_type);
    if (filters.min_range_km) params.append('min_range_km', filters.min_range_km);
    if (filters.max_range_km) params.append('max_range_km', filters.max_range_km);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);

    const queryString = params.toString();
    const endpoint = queryString ? `/weaponeering/ordnance?${queryString}` : '/weaponeering/ordnance';

    return this.request(endpoint);
  }

  /**
   * Get specific ordnance by ID
   * @param {string} ordnanceId - Ordnance identifier
   * @returns {Promise<Object>} Ordnance details
   */
  async getOrdnance(ordnanceId) {
    return this.request(`/weaponeering/ordnance/${ordnanceId}`);
  }

  /**
   * Get ordnance recommendations for target hardness
   * @param {string} hardness - Target hardness (soft, medium, hard, superhard)
   * @param {number} limit - Max results (default 10)
   * @returns {Promise<Array>} Recommended ordnance sorted by effectiveness
   */
  async recommendOrdnanceForHardness(hardness, limit = 10) {
    return this.request(`/weaponeering/ordnance/recommend/for-hardness/${hardness}?limit=${limit}`);
  }

  /**
   * Get ordnance recommendations for target type
   * @param {string} targetType - Target type (building, bunker, etc.)
   * @param {number} limit - Max results (default 10)
   * @returns {Promise<Array>} Recommended ordnance sorted by suitability
   */
  async recommendOrdnanceForTargetType(targetType, limit = 10) {
    return this.request(`/weaponeering/ordnance/recommend/for-target-type/${targetType}?limit=${limit}`);
  }

  // ==================== AI Assistance ====================

  /**
   * Model-proposed site boundary from the site's satellite image (usage
   * imagery.site_boundary). Rejects with error.aiUnavailable (503) or
   * error.aiOutputInvalid (502) when no model-written boundary is available.
   * @param {Object} siteData - Site data for analysis
   * @param {string} siteData.name - Site name
   * @param {Object} siteData.coordinates - Center coordinates {lat, lon}
   * @param {string} siteData.intelligence_summary - Intelligence summary text
   * @param {Array} siteData.facility_locations - Optional facility locations
   * @returns {Promise<Object>} Boundary suggestion with confidence score
   */
  async suggestSiteBoundary(siteData) {
    return this.request('/weaponeering/ai/suggest-site-boundary', {
      method: 'POST',
      body: JSON.stringify(siteData),
    });
  }

  /**
   * Model-proposed JDPIs for a facility (usage jdpi.propose), saved as
   * AI-suggested JDPIs for review. Rejects with error.aiUnavailable (503) or
   * error.aiOutputInvalid (502) when none were model-written.
   * @param {string} facilityId - Facility UUID
   * @returns {Promise<Object>} {dmpis, proposals_not_saved, ai_provenance, collateral_risk_assessment}
   */
  async generateDMPIs(facilityId) {
    return this.request('/weaponeering/ai/generate-dmpis', {
      method: 'POST',
      body: JSON.stringify({
        facility_id: facilityId
      }),
    });
  }

  // ==================== Staleness / Review ====================

  /**
   * Get the weaponeering review queue — DMPIs sorted by staleness urgency.
   * @param {Object} filters - Optional filters
   * @param {string} filters.site_id - Filter by site name or ID
   * @param {string} filters.staleness_status - 'overdue' | 'due_soon' | 'current' | 'unreviewed'
   * @param {number} filters.limit - Max results (default 200)
   * @param {number} filters.offset - Pagination offset
   * @returns {Promise<Array>} Review queue items
   */
  async getReviewQueue(filters = {}) {
    const params = new URLSearchParams();
    if (filters.site_id) params.append('site_id', filters.site_id);
    if (filters.staleness_status) params.append('staleness_status', filters.staleness_status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/weaponeering/review-queue?${queryString}`
      : '/weaponeering/review-queue';

    return this.request(endpoint);
  }

  /**
   * Run AI triage on all overdue DMPIs (batch background task).
   * @returns {Promise<Object>} { triaged_count, summary }
   */
  async runBatchTriage() {
    return this.request('/weaponeering/triage/batch', { method: 'POST' });
  }

  /**
   * Run AI triage on a single DMPI.
   * @param {string} dmpiId - DMPI UUID
   * @returns {Promise<Object>} Updated DMPI with ai_review_priority_score and ai_review_rationale
   */
  async triageDMPI(dmpiId) {
    return this.request(`/weaponeering/triage/${dmpiId}`, { method: 'POST' });
  }

  /**
   * Mark a DMPI as deliberately reviewed, resetting the staleness clock.
   * Sets last_deliberate_review_at = now() and clears ai_review_priority_score.
   * @param {string} dmpiId - DMPI UUID
   * @returns {Promise<Object>} Updated DMPI
   */
  async markDMPIReviewed(dmpiId, { notes, reviewedBy } = {}) {
    return this.request(`/weaponeering/dmpis/${dmpiId}/mark-reviewed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: notes || null, reviewed_by: reviewedBy || null }),
    });
  }

  // ==================== AMD Deployment Planning (SMD demo) ====================

  /**
   * List defended-area sites available for deployment planning (IBCS hub) —
   * lets the operator start a new plan without going through the weaponeering
   * screen. Each entry carries the count of accepted plans already on the site.
   * `profile`: 'amd' (IBCS), 'cuas' (JIATF-401) or 'all' (the default).
   */
  async listAmdSites(profile = 'all') {
    return this.request(`/amd/sites?profile=${encodeURIComponent(profile)}`);
  }

  /**
   * Get one roster of the sensor/effector system-type catalog: 'amd' (the IBCS
   * roster, the default), 'cuas' (the JIATF-401 C-UAS roster) or 'all'.
   */
  async getAmdSystemTypes(profile = 'amd') {
    return this.request(`/amd/system-types?profile=${encodeURIComponent(profile)}`);
  }

  /**
   * Get scenario features (defended assets, threat axes, candidate positions)
   * for a site. Accepts a site UUID or business site_id.
   */
  async getAmdSiteFeatures(siteRef) {
    return this.request(`/amd/sites/${siteRef}/features`);
  }

  /**
   * Get the equipment inventory (on-hand vs authorized, lead times) for the
   * site's COCOM/theater.
   */
  async getAmdSiteInventory(siteRef) {
    return this.request(`/amd/sites/${siteRef}/inventory`);
  }

  /**
   * List all deployment COAs for a site (assignments + accepted gaps included)
   */
  async listDeploymentCoas(siteRef) {
    return this.request(`/amd/sites/${siteRef}/coas`);
  }

  /**
   * Catalog of saved deployment plans across sites (accepted by default) for
   * the Deployment Plans library (card/table views + Dynamic Engagement launch).
   */
  async listDeploymentPlans(status = 'accepted', profile = 'all') {
    return this.request(`/amd/deployment-plans?status=${encodeURIComponent(status)}&profile=${encodeURIComponent(profile)}`);
  }

  /** Full detail for one COA/plan by its UUID (assignments + accepted gaps). */
  async getDeploymentCoa(coaUuid) {
    return this.request(`/amd/coas/${coaUuid}`);
  }

  /**
   * Replace the proposed COA set for a site with freshly generated COAs
   * @param {string} siteRef - site UUID or business site_id
   * @param {Array} coas - CoaPayload objects (see backend app/schemas/amd.py)
   */
  async saveDeploymentCoas(siteRef, coas) {
    return this.request(`/amd/sites/${siteRef}/coas`, {
      method: 'POST',
      body: JSON.stringify({ coas }),
    });
  }

  /**
   * Ask the embedded COA advisor a question about the COAs on screen.
   * @param {{question: string, context: object, history: Array}} payload
   * @returns {Promise<{answer: string, offline: boolean}>}
   */
  async coaAdvisor({ question, context, history = [], persona = null }) {
    return this.request('/amd/coa-advisor', {
      method: 'POST',
      body: JSON.stringify({ question, context, history, persona }),
    });
  }

  /**
   * AI-generated narrative summary of a completed strike run's Battle
   * Rhythm (build-up, peak pressure, outcome) — grounded in the context
   * passed, falls back to a deterministic responder offline.
   * @param {object} context
   * @returns {Promise<{summary: string, offline: boolean}>}
   */
  async strikeSummarize(context) {
    return this.request('/strike/summarize', {
      method: 'POST',
      body: JSON.stringify({ context }),
    });
  }

  /**
   * Time-over-point weather for a planned strike (EP-41): every round judged
   * at every candidate H-Hour by the measured forecast at the time it passes
   * each point, against launch/cruise/terminal limits. The backend fetches
   * the forecast itself; unavailability is reported, never filled in.
   * @param {object} payload - { rounds, candidates: {start, end}, current_h_hour, detail_h_hour }
   */
  async strikeWeatherAssessment(payload) {
    return this.request('/strike/weather-assessment', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** Whether ElevenLabs voice output/input is configured on the backend. */
  async advisorVoiceStatus() {
    return this.request('/amd/advisor/voice-status');
  }

  /** Text-to-speech: returns an audio Blob (MP3) for the given text. */
  async advisorTts(text) {
    const headers = { 'Content-Type': 'application/json' };
    const token = await this.resolveToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const resp = await fetch(`${this.baseURL}/amd/advisor/tts`, { method: 'POST', headers, body: JSON.stringify({ text }) });
    if (!resp.ok) throw new Error(`Voice output failed (${resp.status})`);
    return resp.blob();
  }

  /** Speech-to-text: send recorded audio, get { text }. */
  async advisorStt(blob) {
    const headers = {};
    const token = await this.resolveToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const form = new FormData();
    form.append('file', blob, 'question.webm');
    const resp = await fetch(`${this.baseURL}/amd/advisor/stt`, { method: 'POST', headers, body: form });
    if (!resp.ok) throw new Error(`Voice input failed (${resp.status})`);
    return resp.json();
  }

  /**
   * Accept a COA: persists accepted gaps and rejected positions, archives
   * sibling proposals. This is the state Scenario B reads.
   * @param {string} coaUuid - deployment_coas.id (UUID, not business coa_id)
   */
  async acceptDeploymentCoa(coaUuid, { acceptedBy = null, acceptedGaps = [], rejectedAssignmentIds = [] } = {}) {
    return this.request(`/amd/coas/${coaUuid}/accept`, {
      method: 'POST',
      body: JSON.stringify({
        accepted_by: acceptedBy,
        accepted_gaps: acceptedGaps,
        rejected_assignment_ids: rejectedAssignmentIds,
      }),
    });
  }

  /** Create the next version of a plan lineage (e.g. reinforcements integrated).
   * The base version's rows are never modified; the new version is proposed
   * until accepted. */
  async createCoaVersion(coaUuid, { versionNote, name = null, performanceSummary = null, assignments = [] }) {
    return this.request(`/amd/coas/${coaUuid}/versions`, {
      method: 'POST',
      body: JSON.stringify({
        version_note: versionNote,
        name,
        performance_summary: performanceSummary,
        assignments,
      }),
    });
  }

  /** Version history for a plan lineage (newest first). */
  async getPlanVersions(lineageId) {
    return this.request(`/amd/plans/${lineageId}/versions`);
  }

  /** Revert a plan lineage to an earlier version (pointer flip; prune drops
   * newer versions — the demo reset uses it). dryRun previews the equipment
   * impact (shortfalls vs current inventory) without changing anything. */
  async revertPlanVersion(lineageId, { toVersion = 1, prune = false, dryRun = false } = {}) {
    return this.request(`/amd/plans/${lineageId}/revert`, {
      method: 'POST',
      body: JSON.stringify({ to_version: toVersion, prune, dry_run: dryRun }),
    });
  }

  /**
   * Set protection tiers on a site's defended assets (site-level truth;
   * existing plans keep the snapshot they were generated under).
   * @param {string} siteRef - site UUID or business site_id
   * @param {Array<{feature_id: string, priority: number}>} updates
   */
  async updateAmdAssetPriorities(siteRef, updates, updatedBy = null) {
    return this.request(`/amd/sites/${siteRef}/features/priorities`, {
      method: 'PATCH',
      body: JSON.stringify({ updates, updated_by: updatedBy }),
    });
  }

  /**
   * The JIATF-401 C-sUAS library v4, read-only (systems with their seeded threat rules,
   * threats, fielded packages). Frozen reference data, so one fetch serves the session.
   */
  getCuasCatalog() {
    if (!this._cuasCatalog) {
      this._cuasCatalog = this.request('/amd/cuas/catalog').catch((err) => { this._cuasCatalog = null; throw err; });
    }
    return this._cuasCatalog;
  }

  /**
   * Planner-added defended assets (C-UAS sites only; seeded assets change only by priority).
   * Each call answers the site's refreshed features.
   * @param {{name: string, priority: number, asset_class: string, geometry: object}} asset
   */
  async addAmdAsset(siteRef, asset) {
    return this.request(`/amd/sites/${siteRef}/features/assets`, { method: 'POST', body: JSON.stringify(asset) });
  }

  async updateAmdAsset(siteRef, featureId, changes) {
    return this.request(`/amd/sites/${siteRef}/features/assets/${encodeURIComponent(featureId)}`, {
      method: 'PATCH', body: JSON.stringify(changes),
    });
  }

  async deleteAmdAsset(siteRef, featureId) {
    return this.request(`/amd/sites/${siteRef}/features/assets/${encodeURIComponent(featureId)}`, { method: 'DELETE' });
  }

  /** Start a C-UAS site at a chosen location: { name, latitude, longitude, area, design_threats,
   * starting_force: 'notional'|'empty', place_label }. Returns the new site as the hub lists it. */
  async createCuasSite(site) {
    return this.request('/amd/cuas/sites', { method: 'POST', body: JSON.stringify(site) });
  }

  /** AI-proposed assets inside an area to protect, read from satellite imagery, for the planner to
   * review: { proposals, rejected, limitations, generated_by }. Nothing is stored. */
  async surveyCuasAssets(area, existing = []) {
    return this.request('/amd/cuas/asset-survey', { method: 'POST', body: JSON.stringify({ area, existing }) });
  }

  /** Archive a C-UAS site you started (it leaves the hub; its saved plans are kept). */
  async archiveCuasSite(siteRef) {
    return this.request(`/amd/cuas/sites/${siteRef}/archive`, { method: 'POST', body: '{}' });
  }

  /** Threat approaches (C-UAS sites): a line drawn launch end first. Each returns the refreshed features. */
  async addAmdApproach(siteRef, approach) {
    return this.request(`/amd/sites/${siteRef}/features/approaches`, { method: 'POST', body: JSON.stringify(approach) });
  }

  async deleteAmdApproach(siteRef, featureId) {
    return this.request(`/amd/sites/${siteRef}/features/approaches/${encodeURIComponent(featureId)}`, { method: 'DELETE' });
  }

  /** Candidate positions (C-UAS sites): placed by hand, moved, removed, or stored from the terrain survey. */
  async addAmdCandidate(siteRef, candidate) {
    return this.request(`/amd/sites/${siteRef}/features/candidates`, { method: 'POST', body: JSON.stringify(candidate) });
  }

  async updateAmdCandidate(siteRef, featureId, changes) {
    return this.request(`/amd/sites/${siteRef}/features/candidates/${encodeURIComponent(featureId)}`, {
      method: 'PATCH', body: JSON.stringify(changes),
    });
  }

  async deleteAmdCandidate(siteRef, featureId) {
    return this.request(`/amd/sites/${siteRef}/features/candidates/${encodeURIComponent(featureId)}`, { method: 'DELETE' });
  }

  async replaceAmdSurveyCandidates(siteRef, survey) {
    return this.request(`/amd/sites/${siteRef}/features/candidates/survey`, { method: 'PUT', body: JSON.stringify(survey) });
  }

  /** AI-suggested protection tier per defended asset, with rationale.
   * Read-only — the operator reviews and applies via updateAmdAssetPriorities. */
  async suggestAmdAssetPriorities(siteRef) {
    return this.request(`/amd/sites/${siteRef}/priorities/suggest`, { method: 'POST', body: '{}' });
  }

  /**
   * Generate and persist the emplacement order for an accepted COA. The plan
   * data is loaded server-side; the body carries only the client-observed
   * stress evidence (sim/MC/AAR are ephemeral client state).
   * @param {string} coaUuid - deployment_coas.id (UUID, not business coa_id)
   * @param {{stressEvidence?: object|null, generatedBy?: string|null}} [opts]
   */
  async generateEmplacementOrders(coaUuid, { stressEvidence = null, generatedBy = null } = {}) {
    return this.request(`/amd/coas/${coaUuid}/orders`, {
      method: 'POST',
      body: JSON.stringify({ stress_evidence: stressEvidence, generated_by: generatedBy }),
    });
  }

  /** Order history for one COA version, newest revision first (no bodies). */
  async listEmplacementOrders(coaUuid) {
    return this.request(`/amd/coas/${coaUuid}/orders`);
  }

  /** Reopen a persisted emplacement order (current or historical revision). */
  async getEmplacementOrder(orderUuid) {
    return this.request(`/amd/orders/${orderUuid}`);
  }

  /** Demo scenario: "reinforcements arrived" — state and toggle. */
  async getDemoReinforcements() {
    return this.request('/amd/demo/reinforcements');
  }

  async setDemoReinforcements(enabled) {
    return this.request('/amd/demo/reinforcements', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  }

  // ==================== Health Check ====================

  /**
   * Check if Weaponeering API is reachable
   * @returns {Promise<boolean>} True if API is healthy
   */
  async healthCheck() {
    try {
      await fetch(`${this.baseURL.replace('/api', '')}/health`);
      return true;
    } catch (error) {
      console.warn('Weaponeering API health check failed:', error.message);
      return false;
    }
  }
}

// Export singleton instance
export default new WeaponeeringAPIClient();
